import React, { useEffect, useState } from 'react';
import { Spinner, Table } from 'react-bootstrap';
import { useSelector } from 'react-redux';

import { getOrderAudit } from '../../../services/requests/orders/orders-requests';

// Legacy renders ChangedData with HtmlEncode="false". Recreate its generated
// <br> and <span class="forecasted"> markup without accepting arbitrary HTML.
const renderChangeLine = (text: string) => {
  const parts: React.ReactNode[] = [];
  const spanPattern = /<span\s+class=(['"])(.*?)\1\s*>(.*?)<\/span>/gi;
  let cursor = 0;
  let match: RegExpExecArray | null;

  while ((match = spanPattern.exec(text)) !== null) {
    if (match.index > cursor) parts.push(text.slice(cursor, match.index));
    parts.push(
      <span className={match[2].split(/\s+/).includes('forecasted') ? 'forecasted' : undefined} key={match.index}>
        {match[3]}
      </span>
    );
    cursor = match.index + match[0].length;
  }

  if (cursor < text.length) parts.push(text.slice(cursor));
  return parts;
};

export const renderAuditChange = (text: string) => {
  const parts = String(text).split(/<br\s*\/?>/i);
  return parts.map((part, i) => (
    <React.Fragment key={i}>
      {renderChangeLine(part)}
      {i < parts.length - 1 ? <br /> : null}
    </React.Fragment>
  ));
};

/**
 * Read-only audit trail for the currently loaded order. Reads
 * `state.orders.current.data.id` to determine which order to fetch. Backed by
 * `dbo.AuditRecord` filtered by `EntityType = 'Order'`.
 */
export const OrderAuditTable: React.FC = () => {
  const orderId = useSelector((state: any) => state.orders?.current?.data?.id);
  const [entries, setEntries] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!orderId) {
      setEntries([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    getOrderAudit(orderId)
      .then((data: any[]) => {
        if (!cancelled) setEntries(Array.isArray(data) ? data : []);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [orderId]);

  if (loading) {
    return (
      <div className="d-flex align-items-center p-3">
        <Spinner animation="border" size="sm" className="me-2" />
        <span>Loading audit trail…</span>
      </div>
    );
  }

  if (!entries.length) {
    return <div className="p-3 text-muted">No audit entries.</div>;
  }

  return (
    <Table striped bordered size="sm" hover>
      <thead>
        <tr>
          <th>Timestamp</th>
          <th>User</th>
          <th>Operation</th>
          <th>Change</th>
          <th>IP</th>
        </tr>
      </thead>
      <tbody>
        {entries.map((e) => (
          <tr key={e.id}>
            <td>{e.timestamp ? new Date(e.timestamp).toLocaleString() : ''}</td>
            <td>{e.userDisplayName || e.username || ''}</td>
            <td>{e.operation || ''}</td>
            <td style={{ whiteSpace: 'pre-wrap' }}>{e.changedData ? renderAuditChange(e.changedData) : ''}</td>
            <td>{e.ip || ''}</td>
          </tr>
        ))}
      </tbody>
    </Table>
  );
};

export default OrderAuditTable;
